import React, {Component} from 'react';
import {Text, View} from 'react-native';
import {useLocation, useOutlet} from 'react-router-dom';
import {useLocale} from '../Localization/LocaleContext';
import {checkUserRoleModule} from '../HelperClasses/SingletonClass';
import {MODULEKEYS} from '../Models/Enum';
import RolePermissionContext from '../Component/RolePermissionContext';

// Pricing pages are deliberately outside this map. Plan entitlements control
// what the tenant pays for; this boundary controls admin user permissions.
const ROLE_FREE_PATHS = new Set([
  'upgrade',
  'upgrade-preview',
  'usage-breakdown-preview',
  'workspace/plans',
]);

const normalizePath = pathname =>
  String(pathname || '')
    .replace(/^\/+|\/+$/g, '')
    .replace(/\/+/g, '/');

const isPath = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`);

// Use the existing MODULEKEYS contract. Routes without a dedicated backend
// module stay outside this guard until the module catalog is provisioned. A
// missing module must not be silently treated as GLOBAL: that would block
// otherwise-available pages for users whose role payload does not contain
// `global.manage`.
export const getRouteRoleModule = pathname => {
  const path = normalizePath(pathname);

  if (!path || path === 'dashboard' || path === 'onboarding-may' || path === 'personal-assistant') {
    return null;
  }
  if (ROLE_FREE_PATHS.has(path)) return null;

  if (isPath(path, 'workspace/user-management')) return null;
  if (isPath(path, 'workspace/account') || isPath(path, 'workspace/privacy') || isPath(path, 'workspace/domain')) return null;
  if (isPath(path, 'reports') || isPath(path, 'reported-listings') || isPath(path, 'reported-accounts')) return MODULEKEYS.REPORTS;
  if (isPath(path, 'maps') || isPath(path, 'intelligence') || isPath(path, 'platform-health')) return MODULEKEYS.REPORTS;
  if (isPath(path, 'orders') || isPath(path, 'create-order') || isPath(path, 'order-success') || isPath(path, 'transaction')) return MODULEKEYS.ORDER;
  if (isPath(path, 'inventory') || isPath(path, 'purchase-orders') || isPath(path, 'stock-locations') || isPath(path, 'suppliers') || isPath(path, 'schedule')) return MODULEKEYS.LISTIINGS;
  if (isPath(path, 'accounts') || isPath(path, 'account') || isPath(path, 'account-category') || isPath(path, 'account-attributes') || isPath(path, 'account-configs')) return MODULEKEYS.ACCOUNTS;
  if (isPath(path, 'listings') || isPath(path, 'listing') || isPath(path, 'edit-listing') || isPath(path, 'add-listing') || isPath(path, 'create-listing') || isPath(path, 'legacy-add-listing') || isPath(path, 'add-digital') || isPath(path, 'add-audio')) return MODULEKEYS.LISTIINGS;
  if (isPath(path, 'listing-category') || isPath(path, 'add-listing-category')) return MODULEKEYS.LISTIINGSCATEGORY;
  if (isPath(path, 'add-account-category')) return MODULEKEYS.ACCOUNTSCATEGORY;
  if (isPath(path, 'listing-attributes') || isPath(path, 'attribute-groups') || isPath(path, 'add-attribute') || isPath(path, 'edit-attribute') || isPath(path, 'cart-attributes')) return MODULEKEYS.LISTIINGSATTRIBUTE;
  if (isPath(path, 'user-attributes')) return MODULEKEYS.USERSATTRIBUTE;
  if (isPath(path, 'listing-configs')) return MODULEKEYS.LISTIINGSCONFIGURATION;
  if (isPath(path, 'listing-variants') || isPath(path, 'variant-type')) return MODULEKEYS.LISTIINGSVARIANTTYPES;
  if (isPath(path, 'collections') || isPath(path, 'create-collection') || isPath(path, 'edit-collection') || isPath(path, 'collection')) return MODULEKEYS.COLLECTIONS;
  if (isPath(path, 'promotions') || isPath(path, 'add-promotion') || isPath(path, 'edit-promotion') || isPath(path, 'rewards') || isPath(path, 'reward-programs')) return MODULEKEYS.PROMOS;
  if (isPath(path, 'coupon')) return MODULEKEYS.COUPONS;
  if (isPath(path, 'users') || isPath(path, 'user')) return MODULEKEYS.USERS;
  if (isPath(path, 'media') || isPath(path, 'media-picker')) return MODULEKEYS.MEDIA;
  if (isPath(path, 'review-ratings')) return MODULEKEYS.REVIEWS;
  if (isPath(path, 'delivery')) return MODULEKEYS.SHIPPINGMETHOD;
  if (isPath(path, 'payments')) return MODULEKEYS.PAYMENTMETHOD;
  if (isPath(path, 'commission')) return MODULEKEYS.COMMISSIONS;
  if (isPath(path, 'subscription') || isPath(path, 'membership')) return MODULEKEYS.SUBSCRIPTION;
  if (isPath(path, 'languages')) return MODULEKEYS.LANGUAGE;
  if (isPath(path, 'currency')) return MODULEKEYS.CURRENCY;
  if (isPath(path, 'countries')) return MODULEKEYS.COUNTRY;
  if (isPath(path, 'taxes')) return MODULEKEYS.TAXVIEW;
  if (isPath(path, 'special-fees')) return MODULEKEYS.SPECIALFEES;
  if (isPath(path, 'app-customization')) return MODULEKEYS.APPCONFIG;
  if (isPath(path, 'app-integration') || isPath(path, 'auth') || isPath(path, 'whatsapp') || isPath(path, 'extension')) return MODULEKEYS.NATIVEINTEGRATIONS;
  if (isPath(path, 'strings-customisation')) return MODULEKEYS.TRANSLATIONMANAGE;
  if (isPath(path, 'legacy-communications') || isPath(path, 'communication-2') || isPath(path, 'communications') ) return MODULEKEYS.NOTIFICATIONS;
  if (isPath(path, 'chat-inbox')) return MODULEKEYS.INBOX;
  if (isPath(path, 'api') || isPath(path, 'mcp-connections') || isPath(path, 'mcp-logs')) return MODULEKEYS.APIVIEW;
  if (isPath(path, 'audit-logs')) return null;
  if (isPath(path, 'cms') || isPath(path, 'workspace/templates') || isPath(path, 'workspace/integrations')) return MODULEKEYS.WEBCONFIG;
  if (isPath(path, 'people') || isPath(path, 'workforce') || isPath(path, 'payroll') || isPath(path, 'pay-components') || isPath(path, 'expenses') || isPath(path, 'recurring-payments')) return null;
  if (isPath(path, 'checkouts')) return MODULEKEYS.CHECKOUT;
  if (isPath(path, 'settings') || isPath(path, 'manage_lfn') || isPath(path, 'opensource-starter-kits') || isPath(path, 'workflow') || isPath(path, 'automation-rules')) return null;
  if (isPath(path, 'bundles')) return MODULEKEYS.LISTIINGS;
  if (isPath(path, 'test') || isPath(path, 'sentry-check') || isPath(path, 'gamification-test') || isPath(path, 'cms-test') || isPath(path, 'apps')) return null;

  return null;
};

export class RoleProtectedPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      permissionsLoaded: false,
      readP: false,
      writeP: false,
      deleteP: false,
    };
  }

  componentDidMount() {
    checkUserRoleModule(this.props.moduleKey, res => {
      this.setState({
        permissionsLoaded: true,
        readP: res[0],
        writeP: res[1],
        deleteP: res[2],
      });
    });
  }

  renderNoAccess = () => (
    <View style={{flex: 1, minHeight: 280, alignItems: 'center', justifyContent: 'center', padding: 32}}>
      <Text style={{fontSize: 22, fontWeight: '700', color: 'var(--color-text)'}}>
        {this.props.translate('common.no_access_to_page', 'No access')}
      </Text>
      <Text style={{marginTop: 8, textAlign: 'center', color: 'var(--color-text-secondary)'}}>
        {this.props.translate(
          'common.permission_to_view_page_denied',
          'You do not have permission to view this page.',
        )}
      </Text>
    </View>
  );

  renderLoading = () => (
    <View style={{flex: 1, minHeight: 180, alignItems: 'center', justifyContent: 'center'}}>
      <Text style={{color: 'var(--color-text-secondary)'}}>
        {this.props.translate('common.loading_permissions', 'Loading permissions…')}
      </Text>
    </View>
  );

  render() {
    const {children} = this.props;
    const {permissionsLoaded, readP, writeP, deleteP} = this.state;
    const permission = {readP, writeP, deleteP, moduleKey: this.props.moduleKey};

    if (!permissionsLoaded) return this.renderLoading();
    if (!readP) return this.renderNoAccess();

    const page = React.isValidElement(children)
      ? React.cloneElement(children, {readP, writeP, deleteP})
      : children;

    return (
      <RolePermissionContext.Provider value={permission}>
        {page}
      </RolePermissionContext.Provider>
    );
  }
}

const RoleProtectedOutlet = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const {translate} = useLocale();
  const moduleKey = getRouteRoleModule(location.pathname);

  if (!moduleKey) return outlet;
  return <RoleProtectedPage moduleKey={moduleKey} translate={translate}>{outlet}</RoleProtectedPage>;
};

export default RoleProtectedOutlet;
